import { Link } from "react-router-dom";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { FiX, FiArrowUpRight, FiHome, FiDollarSign, FiTrendingUp, FiPercent, FiTool, FiBriefcase, FiFileText, FiPieChart, FiBarChart2, FiUser } from "@/lib/icons";

interface MobileNavProps {
  open: boolean;
  onClose: () => void;
}

const navSections = [
  {
    title: "Home Loans",
    items: [
      { label: "Buying a Home", href: "/home-loans/buying-a-home", icon: FiHome },
      { label: "First Home Buyer", href: "/home-loans/first-home-buyer", icon: FiUser },
      { label: "Refinancing", href: "/home-loans/refinancing", icon: FiTrendingUp },
      { label: "Investment", href: "/home-loans/investment", icon: FiDollarSign },
      { label: "Construction", href: "/home-loans/construction", icon: FiTool },
    ],
  },
  {
    title: "Business",
    items: [
      { label: "Asset Finance", href: "/business-loans/asset-finance", icon: FiBriefcase },
      { label: "Invoice Finance", href: "/business-loans/invoice-finance", icon: FiFileText },
      { label: "Term Loans", href: "/business-loans/term-loans", icon: FiDollarSign },
    ],
  },
  {
    title: "Resources",
    items: [
      { label: "Calculators", href: "/calculators", icon: FiPieChart },
      { label: "Current Rates", href: "/rates", icon: FiPercent },
      { label: "Guides", href: "/guides", icon: FiFileText },
      { label: "Blog", href: "/blog", icon: FiBarChart2 },
    ],
  },
];

export function MobileNav({ open, onClose }: MobileNavProps) {
  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md bg-primary border-none p-0 [&>button]:hidden"
      >
        <div className="flex flex-col h-full text-primary-foreground">
          {/* Header with Logo and Close */}
          <div className="flex items-center justify-between p-4">
            <Link to="/" onClick={onClose}>
              <img
                src="/logo/PATH_FinanceSolutions_LogoLong_Normal_REV.png"
                alt="Path Financial Solutions"
                className="h-10 w-auto"
              />
            </Link>
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
              aria-label="Close menu"
            >
              <FiX className="size-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto px-6 pb-6">
            {navSections.map((section) => (
              <div key={section.title} className="mb-8">
                <h3 className="font-serif text-2xl mb-4">{section.title}</h3>
                <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                  {section.items.map((item) => {
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.href}
                        to={item.href}
                        onClick={onClose}
                        className="flex items-center gap-2 text-primary-foreground/80 hover:text-primary-foreground transition-colors"
                      >
                        <Icon className="size-4 text-accent shrink-0" />
                        <span className="text-sm">{item.label}</span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* Company Links */}
            <div className="mb-8">
              <h3 className="font-serif text-2xl mb-4">Company</h3>
              <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                <Link
                  to="/about"
                  onClick={onClose}
                  className="flex items-center gap-2 text-primary-foreground/80 hover:text-primary-foreground transition-colors"
                >
                  <FiUser className="size-4 text-accent shrink-0" />
                  <span className="text-sm">About Us</span>
                </Link>
                <Link
                  to="/lenders"
                  onClick={onClose}
                  className="flex items-center gap-2 text-primary-foreground/80 hover:text-primary-foreground transition-colors"
                >
                  <FiBriefcase className="size-4 text-accent shrink-0" />
                  <span className="text-sm">Our Lenders</span>
                </Link>
                <Link
                  to="/contact"
                  onClick={onClose}
                  className="flex items-center gap-2 text-primary-foreground/80 hover:text-primary-foreground transition-colors"
                >
                  <FiFileText className="size-4 text-accent shrink-0" />
                  <span className="text-sm">Contact</span>
                </Link>
              </div>
            </div>
          </nav>

          {/* Bottom Section */}
          <div className="p-6 border-t border-white/10">
            {/* Contact Card with Background Image */}
            <Link
              to="/contact"
              onClick={onClose}
              className="block relative rounded-2xl overflow-hidden mb-6 group"
            >
              <div className="aspect-[2/1] relative">
                {/* Background Image */}
                <img
                  src="/content/home-loan.jpeg"
                  alt="Get in touch"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
                {/* Overlay */}
                <div className="absolute inset-0 bg-primary/60 group-hover:bg-primary/50 transition-colors" />
                {/* Content */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="flex items-center gap-2 text-primary-foreground font-medium">
                    Contact Us
                    <FiArrowUpRight className="size-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </div>
              </div>
            </Link>

            {/* Legal Link */}
            <div className="flex items-center justify-end">
              {/* Social links - commented out for now
              <div className="flex gap-4">
                <a href="#" className="text-primary-foreground/85 hover:text-primary-foreground transition-colors" aria-label="Facebook">
                  <FiFacebook className="size-5" />
                </a>
                <a href="#" className="text-primary-foreground/85 hover:text-primary-foreground transition-colors" aria-label="LinkedIn">
                  <FiLinkedin className="size-5" />
                </a>
                <a href="#" className="text-primary-foreground/85 hover:text-primary-foreground transition-colors" aria-label="Instagram">
                  <FiInstagram className="size-5" />
                </a>
              </div>
              */}
              <Link
                to="/terms-conditions"
                onClick={onClose}
                className="text-xs text-primary-foreground/80 hover:text-primary-foreground/80 transition-colors"
              >
                Terms of service
              </Link>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
